import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import Button from "../components/common/Button";
import {
  directNavigationItems,
  superAdminDirectItems,
  nestedGroups,
} from "../config/navigation";
import {
  getUserModules,
  isSuperAdmin,
  getVisibleNavigationItems,
  shouldShowNestedGroup,
} from "../utils/navigationUtils";

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const { profile, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clicking the menu item for the page you're already on should refresh it.
  // React Router's <Link> is a no-op when navigating to the current route, so
  // we intercept and force a real reload (also clears Apollo's in-memory cache).
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    route: string
  ) => {
    if (location.pathname === route) {
      e.preventDefault();
      window.location.reload();
    }
  };

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to logout?")) {
      try {
        await logout();
      } catch (error) {
        // Logout error is handled by logout function
      }
    }
  };

  const isSuperAdminUser = useMemo(
    () => isSuperAdmin(profile?.role),
    [profile?.role]
  );

  const userModules = useMemo(() => {
    const permissions: string[] =
      profile && Array.isArray(profile.permissions) ? profile.permissions : [];
    return getUserModules(permissions);
  }, [profile]);

  // Get visible direct navigation items
  const visibleDirectItems = useMemo(() => {
    const direct = getVisibleNavigationItems(
      directNavigationItems,
      userModules,
      isSuperAdminUser
    );

    // For superadmin, add special direct items
    const superAdminItems = isSuperAdminUser
      ? getVisibleNavigationItems(
          superAdminDirectItems,
          userModules,
          isSuperAdminUser
        )
      : [];

    return [...direct, ...superAdminItems];
  }, [userModules, isSuperAdminUser]);

  // Get visible nested groups
  const visibleNestedGroups = useMemo(() => {
    return nestedGroups.filter((group) =>
      shouldShowNestedGroup(group, userModules, isSuperAdminUser)
    );
  }, [userModules, isSuperAdminUser]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Top Navigation Bar */}
      <header className="w-full h-[10vh] bg-background-dark text-white flex items-center justify-between px-6 shadow-md sticky top-0 z-50">
        <div className="flex items-center space-x-10">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center">
            <img
              src="/assets/images/logo.png"
              alt="Logo"
              className="h-8 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/logo192.png";
              }}
            />
          </Link>

          {/* Navigation Links */}
          <nav className="flex space-x-6 text-md font-medium">
            {/* Overview - always visible for authenticated users */}
            <Link
              to="/dashboard"
              onClick={(e) => handleNavClick(e, "/dashboard")}
              className={`border-b-2 transition-colors ${
                location.pathname.startsWith("/dashboard")
                  ? "border-white text-white"
                  : "border-transparent text-gray-300 hover:text-white"
              }`}
            >
              Overview
            </Link>

            {/* Direct navigation items */}
            {visibleDirectItems
              .filter((item) => item.id !== "dashboard")
              .map((item) => {
                const isActive = location.pathname.startsWith(item.route);
                return (
                  <Link
                    key={item.id}
                    to={item.route}
                    onClick={(e) => handleNavClick(e, item.route)}
                    className={`border-b-2 transition-colors ${
                      isActive
                        ? "border-white text-white"
                        : "border-transparent text-gray-300 hover:text-white"
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}

            {/* Nested navigation groups */}
            {visibleNestedGroups.map((group) => {
              const isActive = location.pathname.startsWith(group.route);
              return (
                <Link
                  key={group.id}
                  to={group.route}
                  onClick={(e) => handleNavClick(e, group.route)}
                  className={`border-b-2 transition-colors ${
                    isActive
                      ? "border-white text-white"
                      : "border-transparent text-gray-300 hover:text-white"
                  }`}
                >
                  {group.name}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="h-10 w-10 rounded-full bg-primary-default text-white flex items-center justify-center font-bold text-lg shadow-md hover:opacity-90 transition"
          >
            {profile?.name?.[0]?.toUpperCase()}
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-60 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4 animate-fade-in-up">
              <p className="text-base font-semibold text-gray-900">
                {profile?.name}
              </p>
              <p className="text-xs text-primary-default capitalize mb-3">
                {profile?.role?.replace("_", " ")}
              </p>
              <Link
                to="/profile"
                onClick={() => setDropdownOpen(false)}
                className="block w-full mb-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
              >
                View Profile
              </Link>
              <Button
                variant="danger"
                size="sm"
                onClick={handleLogout}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full h-[90vh]">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
